import { useLayoutEffect, useRef, useState } from 'react';
import { fitScreenWidth } from '@/lib/app-editor/device-fit';
import { cn } from '@/lib/utils';

export interface CanvasViewportProps {
  frameAspect: number;
  /** Below the device (platform toggle, "Ver no meu site"). */
  toolbar?: React.ReactNode;
  children: (size: { screenWidth: number }) => React.ReactNode;
  className?: string;
}

const PADDING_PX = 16;
const MIN_SCREEN_WIDTH = 160;
const MAX_SCREEN_WIDTH = 320;

/** Measures the space left for the device and hands the children the screen width that fits. */
export function CanvasViewport({
  frameAspect,
  toolbar,
  children,
  className,
}: CanvasViewportProps) {
  const areaRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const rect = area.getBoundingClientRect();
    setSize({ width: rect.width, height: rect.height });
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(area);
    return () => observer.disconnect();
  }, []);

  const screenWidth = fitScreenWidth({
    containerWidth: size.width,
    containerHeight: size.height,
    frameAspect,
    padding: PADDING_PX,
    min: MIN_SCREEN_WIDTH,
    max: MAX_SCREEN_WIDTH,
  });

  return (
    <div className={cn('flex h-full min-h-0 w-full flex-col', className)}>
      <div ref={areaRef} className="flex min-h-0 flex-1 overflow-auto">
        {/* `m-auto` centres the device and, unlike `items-center`, never pushes its top out of the scrollable area. */}
        <div className="m-auto">{children({ screenWidth })}</div>
      </div>
      {toolbar && (
        <div className="flex shrink-0 justify-center pb-2">{toolbar}</div>
      )}
    </div>
  );
}
